import { useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';

export default function SchemaValidator() {
  const [jsonInput, setJsonInput] = useState('');
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    message: string;
    details?: any;
  } | null>(null);

  const validateJSON = () => {
    if (!jsonInput.trim()) {
      setValidationResult({
        isValid: false,
        message: 'Please enter JSON to validate',
      });
      return;
    }

    try {
      const parsed = JSON.parse(jsonInput);

      // Additional validation checks
      const checks = {
        objectCount: countObjects(parsed),
        arrayCount: countArrays(parsed),
        maxDepth: getMaxDepth(parsed),
        totalKeys: countKeys(parsed),
        dataTypes: getDataTypes(parsed),
      };

      setValidationResult({
        isValid: true,
        message: 'Valid JSON! Ready for conversion',
        details: checks,
      });
    } catch (error: any) {
      setValidationResult({
        isValid: false,
        message: error.message,
      });
    }
  };

  const countObjects = (obj: any): number => {
    let count = 0;
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      count = 1;
      Object.values(obj).forEach((val) => {
        count += countObjects(val);
      });
    } else if (Array.isArray(obj)) {
      obj.forEach((item) => {
        count += countObjects(item);
      });
    }
    return count;
  };

  const countArrays = (obj: any): number => {
    let count = 0;
    if (Array.isArray(obj)) {
      count = 1;
      obj.forEach((item) => {
        count += countArrays(item);
      });
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach((val) => {
        count += countArrays(val);
      });
    }
    return count;
  };

  const getMaxDepth = (obj: any, depth = 0): number => {
    if (obj && typeof obj === 'object') {
      const depths = Object.values(obj).map((val) => getMaxDepth(val, depth + 1));
      return Math.max(depth, ...depths);
    }
    return depth;
  };

  const countKeys = (obj: any): number => {
    let count = 0;
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      count = Object.keys(obj).length;
      Object.values(obj).forEach((val) => {
        count += countKeys(val);
      });
    } else if (Array.isArray(obj)) {
      obj.forEach((item) => {
        count += countKeys(item);
      });
    }
    return count;
  };

  const getDataTypes = (obj: any): Record<string, number> => {
    const types: Record<string, number> = {};

    const countTypes = (val: any) => {
      const type = Array.isArray(val) ? 'array' : typeof val;
      types[type] = (types[type] || 0) + 1;

      if (val && typeof val === 'object') {
        Object.values(val).forEach(countTypes);
      }
    };

    countTypes(obj);
    return types;
  };

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-1">
          Schema Validator
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-muted">
          Validate JSON structure before conversion
        </p>
      </div>

      <textarea
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        placeholder='Paste your JSON here...'
        className="w-full h-40 p-3 border border-gray-300 dark:border-dark-border rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-500 resize-none"
      />

      <button
        onClick={validateJSON}
        className="mt-3 w-full bg-gradient-to-r from-coral-500 to-teal-500 text-white py-2.5 rounded-lg font-medium hover:shadow-lg transition-all"
      >
        Validate Schema
      </button>

      {validationResult && (
        <div
          className={`mt-4 p-4 rounded-lg ${
            validationResult.isValid
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-start gap-3">
            {validationResult.isValid ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p
                className={`text-sm font-medium ${
                  validationResult.isValid ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'
                }`}
              >
                {validationResult.message}
              </p>

              {validationResult.details && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white dark:bg-dark-bg p-2 rounded">
                      <span className="text-gray-600 dark:text-dark-muted">Objects:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-dark-text">
                        {validationResult.details.objectCount}
                      </span>
                    </div>
                    <div className="bg-white dark:bg-dark-bg p-2 rounded">
                      <span className="text-gray-600 dark:text-dark-muted">Arrays:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-dark-text">
                        {validationResult.details.arrayCount}
                      </span>
                    </div>
                    <div className="bg-white dark:bg-dark-bg p-2 rounded">
                      <span className="text-gray-600 dark:text-dark-muted">Max Depth:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-dark-text">
                        {validationResult.details.maxDepth}
                      </span>
                    </div>
                    <div className="bg-white dark:bg-dark-bg p-2 rounded">
                      <span className="text-gray-600 dark:text-dark-muted">Total Keys:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-dark-text">
                        {validationResult.details.totalKeys}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-dark-bg p-2 rounded">
                    <p className="text-xs text-gray-600 dark:text-dark-muted mb-1">Data Types:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(validationResult.details.dataTypes).map(
                        ([type, count]) => (
                          <span
                            key={type}
                            className="px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-xs"
                          >
                            {type}: {count as number}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
